'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, MessageSquare, Layers } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof MessageSquare;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: 'Conversation Management',
    icon: MessageSquare,
    body: (
      <>
        <p className="text-base">
          Voley gives your agency a single shared phone number for all client communication.
          To your clients, every message comes from the same number — but on your side,
          you have full visibility into who sent each message.
        </p>
        <p className="text-[15px]">
          This tour will walk you through how conversations are managed, organized,
          and kept under control across your entire team.
        </p>
      </>
    ),
  },
  {
    heading: 'What You\'ll Learn',
    icon: Layers,
    body: (
      <>
        <p className="text-base">
          In the next few steps, we&rsquo;ll cover:
        </p>
        <div className="flex flex-col gap-2 pl-4">
          <div className="flex items-center gap-2">
            <div className="h-1 w-1 rounded-full bg-accent-blue" />
            <span className="text-[15px] font-semibold text-gray-900">How your shared agency number works</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1 w-1 rounded-full bg-accent-blue" />
            <span className="text-[15px] font-semibold text-gray-900">Opening and closing conversations</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1 w-1 rounded-full bg-accent-blue" />
            <span className="text-[15px] font-semibold text-gray-900">Filtering by unread messages</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1 w-1 rounded-full bg-accent-blue" />
            <span className="text-[15px] font-semibold text-gray-900">Navigating Assigned &amp; Unassigned views</span>
          </div>
        </div>
      </>
    ),
  },
];

interface ConversationMgmtWelcomeModalProps {
  onComplete: () => void;
}

export function ConversationMgmtWelcomeModal({ onComplete }: ConversationMgmtWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Conversation Management Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let's Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
